import { computed, inject, Injectable, signal } from '@angular/core';
import { StorageKey } from '@shared/enums/storage-keys.enum';
import { StorageService } from '../../../shared/services/storage.service';
import {
  rxResource,
  takeUntilDestroyed,
  toObservable,
} from '@angular/core/rxjs-interop';
import { ProbateService } from '../../../probate/shared/services/probate.service';
import { SubscribersHttpService } from './subscribers-http.service';
import { filter, of, tap } from 'rxjs';
import { ToastService } from '@shared/components/toast';
import { ERROR_TOAST_GET_SUBSCRIBERS } from '../../../shared/constants/toasts/subscriber-http/constants/toast-messages.constants';
import { CaseContextService } from '@app/core/services/case-context/case-context.service';
import { TenantState } from '@shared/features/tenant/shared/services/tenant-state.service';
import { AuthService } from '@app/core/services/auth/auth.service';
import { TenantFacadeService } from '@shared/features/tenant/shared/services/tenant-facade.service';
import { TenantKey } from '@app/core/enums/tenant-key.enum';

@Injectable({ providedIn: 'root' })
export class SubscribersStateService {
  private readonly _storage = inject(StorageService);
  private readonly _toast = inject(ToastService);
  private readonly _tenantState = inject(TenantState);
  private readonly _authService = inject(AuthService);
  private readonly _tenantFacade = inject(TenantFacadeService);

  private readonly _probateService = inject(ProbateService);
  private readonly _subscriberService = inject(SubscribersHttpService);
  private readonly _caseContext = inject(CaseContextService);

  private readonly _selectedSubscriberId = signal<number | null>(
    this._storage.read(StorageKey.selectedSubscriberId),
  );

  public selectedSubscriberId = this._selectedSubscriberId.asReadonly();

  public setSelectedSubscriberId(subscriberId: number) {
    this._storage.save(StorageKey.selectedSubscriberId, subscriberId);
    this._selectedSubscriberId.set(subscriberId);
    // Sync to case context - single source of truth for subscriber ID
    this._caseContext.setContext({ subscriberId });
  }

  private readonly _subscribersResource = rxResource({
    params: () => ({
      probateId: this._probateService.probateId(),

      tenant: this._tenantFacade.currentTenantKey(),
    }),
    defaultValue: this._storage.read(StorageKey.subscribers),
    stream: ({ params }) => {
      const { probateId, tenant } = params;

      const isProbateTenant = tenant === TenantKey.PROBATE;
      if (!probateId || !isProbateTenant) return of([]);

      return this._subscriberService.getSubscribers(Number(probateId));
    },
  });

  public readonly subscribers = this._subscribersResource.value;

  public readonly isLoading = this._subscribersResource.isLoading;

  public reload() {
    this._subscribersResource.reload();
  }

  public loggedSubscriber = computed(() => {
    return this.subscribers()?.find(
      (subscriber) =>
        subscriber.email === this._authService.sessionUser()?.email,
    );
  });

  constructor() {
    toObservable(this._subscribersResource.error)
      .pipe(
        filter((isError) => !!isError),
        tap(() => {
          this._storage.remove(StorageKey.subscribers);
        }),
        tap(() => {
          this._toast.show(ERROR_TOAST_GET_SUBSCRIBERS);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
}
